import React, { useEffect, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { flattenErrors } from './ErrorHelpers';
import axios from 'axios';
import moment from 'moment';

export function UpdateRecurringPaymentsComponent({ accountNumber, children }) {
    const [modalSettings, setModalSettings] = useState({
        showModal: false,
        renderModal: false
    });

    return (
        <>
            <div onClick={() => setModalSettings({
                showModal: true,
                renderModal: true
            })}>
                {children}
            </div>
            {modalSettings.renderModal &&
                <UpdateRecurringPaymentsModalComponent
                    accountNumber={accountNumber}
                    showModal={modalSettings.showModal}
                    handleClose={() => setModalSettings({ showModal: false })}
                    onExited={() => setModalSettings({ renderModal: false })} />}
        </>
    );
}

function UpdateRecurringPaymentsModalComponent({ accountNumber, showModal, handleClose, onExited }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [recurringPaymentInfo, setRecurringPaymentInfo] = useState({});
    const [errors, setErrors] = useState([]);
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);

    useEffect(() => {
        axios.get(`/api/talent/${accountNumber}/recurringpayments`).then(resp => {
            setLoading(false);
            setRecurringPaymentInfo(resp.data);
        });
    }, []);

    function modalWithContent(children) {
        return (
            <Modal isOpen={showModal} onClosed={onExited}>
                <ModalHeader toggle={handleClose}>
                    Update Recurring Payments
                </ModalHeader>
                <ModalBody>
                    {children}
                </ModalBody>
            </Modal>
        );
    }

    if (loading) {
        return modalWithContent(<div className="text-center">Loading...</div>);
    }

    if (recurringPaymentInfo.canUpdateRecurringPayments === false) {
        return modalWithContent(<div className="text-center">{recurringPaymentInfo.message}</div>);
    }

    if (showSuccessScreen) {
        return modalWithContent(<div className="text-center">Recurring payments updated successfully!</div>);
    }

    const amount = parseFloat(recurringPaymentInfo.amountOfPayments);
    const numberOfRemainingRecurringPayments = recurringPaymentInfo.numberOfRemainingRecurringPayments;
    const nextPaymentDateFormatted = new moment(recurringPaymentInfo.nextPaymentDate).format("MMMM DD, YYYY");
    const currentCreditCardNumber = recurringPaymentInfo.currentCreditCardNumber;

    function save() {
        setSaving(true);
        setErrors([]);
        axios.get(`/api/talent/${accountNumber}/recurringpayments/change`).then(resp => {
                if (resp.data && resp.data.redirectUrl) {
                    window.location.href = resp.data.redirectUrl;
                } else {
                    setShowSuccessScreen(true);
                }
            }).catch(error => {
                if (error.response) {
                    if (error.response.status === 400) {
                        if (typeof error.response.data === "string") {
                            setErrors([error.response.data]);
                        } else {
                            setErrors(flattenErrors(error.response.data.errors));
                        }
                    }
                } else if (error.request) {
                    console.log(error.request);
                }
            })
            .then(() => setSaving(false));
    }

    return (
        <Modal isOpen={showModal} onClosed={onExited}>
            <ModalHeader toggle={handleClose}>
                Update Recurring Payments
            </ModalHeader>
            <ModalBody>
                {recurringPaymentInfo.canUpdateRecurringPayments && <div>
                    <h4>Current Recurring Payments</h4>
                    <div className="row">
                        <div className="col-sm-6 bold">
                            Current Card Number:
                        </div>
                        <div className="col-sm-6">
                            {currentCreditCardNumber}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6 bold">
                            # Remaining:
                        </div>
                        <div className="col-sm-6">
                            {numberOfRemainingRecurringPayments}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6 bold">
                            Amount:
                        </div>
                        <div className="col-sm-6">
                            ${amount.toFixed(2)}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6 bold">
                            Next Payment Date:
                        </div>
                        <div className="col-sm-6">
                            {nextPaymentDateFormatted}
                        </div>
                    </div>

                    <hr />

                    <div className="d-flex justify-content-between align-items-center">
                        <h4>Update Recurring Payment Card</h4>
                        <a href="https://stripe.com" target="_blank" rel="noopener noreferrer">
                            <img src="/Powered_by_Stripe.svg" style={{ height: '30px' }} alt="Stripe Seal" />
                        </a>
                    </div>

                    <p>
                        Click the button below to update your credit card information for recurring payments.
                    </p>
                    <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="accept-terms" checked={acceptTerms}
                            disabled={saving} onChange={e => setAcceptTerms(e.target.checked)} />
                        <label className="form-check-label" htmlFor="accept-terms">
                            I agree to change my recurring payment to the credit card above.
                            My card will be charged {numberOfRemainingRecurringPayments} times
                            in the amount of ${amount.toFixed(2)}{' '}
                            on a {recurringPaymentInfo.interval} basis
                            starting on {nextPaymentDateFormatted}.
                        </label>
                    </div>
                </div>}
            </ModalBody>
            <ModalFooter>
                {errors && <div className="float-left text-left">
                    {errors.map(e => <div key={e}>{e}</div>)}
                </div>}
                <Button
                    onClick={save}
                    disabled={saving || !acceptTerms}>
                    Update Card
                </Button>
            </ModalFooter>
        </Modal>
    );
}